import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  ArrowRight,
  CheckCircle2,
  MousePointerClick,
  SlidersHorizontal,
  AlertTriangle,
  Zap,
  Server,
  Users,
  Database,
  Layers,
  AppWindow,
  Cloud,
  HardDrive,
  Network,
  Clock,
  Gauge,
  ShieldAlert,
  Bell,
  Ticket,
  Activity,
  Wrench,
} from "lucide-react";
import SEO from "../components/site/SEO";
import SectionHeader from "../components/site/SectionHeader";

// ============ Data =============

const STEPS = [
  {
    n: "01",
    icon: MousePointerClick,
    title: "Choose",
    body: "Select the technologies and infrastructure components you want to monitor.",
  },
  {
    n: "02",
    icon: SlidersHorizontal,
    title: "Configure",
    body: "Define monitoring methods, intervals, thresholds and conditions.",
  },
  {
    n: "03",
    icon: ShieldAlert,
    title: "Prioritise",
    body: "Assign Critical, High, Medium, Low or Informational priority.",
  },
  {
    n: "04",
    icon: Zap,
    title: "Respond",
    body:
      "Receive alerts and take action before infrastructure issues become business problems.",
  },
];

const TECH_GRID = [
  {
    icon: Server,
    title: "Servers",
    items: ["Windows Servers", "Linux Servers", "Physical Servers", "Virtual Machines"],
  },
  {
    icon: Users,
    title: "Enterprise Services",
    items: [
      "Active Directory",
      "Domain Controllers",
      "DNS",
      "DHCP",
      "Microsoft Exchange",
      "Microsoft 365",
    ],
  },
  {
    icon: Database,
    title: "Databases",
    items: ["Microsoft SQL Server", "Enterprise Databases"],
  },
  {
    icon: Layers,
    title: "Virtualisation",
    items: ["VMware", "Hyper-V"],
  },
  {
    icon: AppWindow,
    title: "Applications",
    items: ["Web Applications", "APIs", "Business Applications"],
  },
  {
    icon: Cloud,
    title: "Cloud",
    items: ["Cloud Infrastructure", "Cloud Services"],
  },
  {
    icon: HardDrive,
    title: "Storage & Backup",
    items: ["Storage Infrastructure", "Backup Infrastructure"],
  },
  {
    icon: Network,
    title: "Enterprise Infrastructure",
    items: ["Managed Switches", "Wireless Controllers"],
  },
];

const LIGHTWEIGHT = [
  "Lightweight architecture",
  "Efficient monitoring",
  "Configurable monitoring intervals",
  "Reduced infrastructure overhead",
  "Focused monitoring",
  "Flexible deployment",
];

const PRIORITY_TIERS = [
  {
    lvl: "CRITICAL",
    color: "#EF4444",
    items: ["Production Database", "Domain Controller", "Business-Critical Application"],
  },
  {
    lvl: "HIGH",
    color: "#F59E0B",
    items: ["Application Server", "Exchange Server", "Backup Server"],
  },
  {
    lvl: "MEDIUM",
    color: "#00F0FF",
    items: ["File Server", "Internal Application"],
  },
  {
    lvl: "LOW",
    color: "#8B93A5",
    items: ["Development Server", "Non-critical Service"],
  },
];

const CUSTOMISE = [
  "What to monitor",
  "Monitoring frequency",
  "Monitoring conditions",
  "Thresholds",
  "Priority",
  "Alert conditions",
  "Notification rules",
  "Escalation rules",
];

// ============ Page ============

export default function InfraMonitoring() {
  return (
    <div data-testid="infra-monitoring-page" className="bg-[#050507] text-white">
      <SEO
        title="IT Infrastructure Monitoring | Quasect"
        description="Lightweight and highly customisable IT infrastructure monitoring for servers, applications, databases, cloud and enterprise services."
        keywords="IT Infrastructure Monitoring, Infrastructure Monitoring, Server Monitoring, Application Monitoring, Database Monitoring, Lightweight Infrastructure Monitoring, Enterprise IT Monitoring, IT Infrastructure Management, Custom IT Monitoring"
        path="/it-infrastructure-monitoring"
      />

      {/* ============== HERO ============== */}
      <section className="relative overflow-hidden noise border-b border-white/10">
        <div className="absolute inset-0 bg-grid bg-grid-fade opacity-50" />
        <div className="absolute -top-40 -right-40 w-[700px] h-[700px] rounded-full opacity-20 blur-3xl"
             style={{ background: "radial-gradient(circle, #00F0FF 0%, transparent 70%)" }} />
        <div className="absolute -bottom-40 -left-40 w-[600px] h-[600px] rounded-full opacity-15 blur-3xl"
             style={{ background: "radial-gradient(circle, #39FF14 0%, transparent 70%)" }} />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 pt-24 pb-20 md:pt-32 md:pb-24 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="font-mono text-xs tracking-[0.3em] uppercase text-[#39FF14] flex items-center gap-3"
            >
              <span className="w-6 h-px bg-[#39FF14]" /> NEW · IT INFRASTRUCTURE MONITORING
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05, duration: 0.8 }}
              data-testid="infra-hero-headline"
              className="font-display font-black mt-6 text-4xl sm:text-5xl md:text-6xl lg:text-[64px] tracking-tighter leading-[1.05] break-words"
            >
              IT Infrastructure <br />
              <span className="shimmer-text">Monitoring.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="mt-5 font-mono text-sm md:text-base tracking-[0.25em] uppercase text-[#00F0FF]"
            >
              Lightweight. Highly Customisable.
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.7 }}
              className="mt-7 text-lg md:text-xl text-[#8B93A5] leading-relaxed max-w-2xl"
            >
              Quasect helps businesses continuously monitor their critical IT infrastructure.
              Choose the technologies, systems and services that matter to your organisation,
              define how they should be monitored, and prioritise the systems that require the
              most attention.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mt-9 flex flex-wrap gap-4"
            >
              <Link
                to="/contact"
                data-testid="infra-cta-demo"
                className="btn-sweep bg-[#39FF14] text-black font-bold uppercase tracking-wider px-7 py-4 text-sm hover:bg-[#32E612] transition-colors flex items-center gap-2"
              >
                Request a Demo <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                data-testid="infra-cta-talk"
                className="border border-white/30 hover:border-white text-white font-medium px-7 py-4 text-sm uppercase tracking-wider transition-all flex items-center gap-2"
              >
                Talk to Quasect
              </Link>
            </motion.div>
          </div>

          {/* Hero visual — mini dashboard preview */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="lg:col-span-5"
          >
            <MiniDashboard />
          </motion.div>
        </div>
      </section>

      {/* ============== YOU DECIDE WHAT MATTERS ============== */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <SectionHeader
            overline="How It Works"
            title="You Decide What Matters. Quasect Monitors It."
            subtitle="Every organisation has a different IT environment. Quasect allows you to decide which technologies, systems and services are important to your business and configure monitoring around those priorities."
          />
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ delay: i * 0.08 }}
                className="tracing-card group bg-[#0A0A0D] border border-white/10 hover:border-[#00F0FF]/50 p-6 md:p-7 transition-all relative"
                data-testid={`infra-step-${s.n}`}
              >
                <div className="flex items-center justify-between">
                  <div className="w-11 h-11 border border-white/15 flex items-center justify-center group-hover:border-[#39FF14] transition-colors">
                    <s.icon className="w-5 h-5 text-[#00F0FF] group-hover:text-[#39FF14] transition-colors" />
                  </div>
                  <span className="font-mono text-[10px] tracking-widest text-[#8B93A5]">
                    {s.n} / 04
                  </span>
                </div>
                <h3 className="font-display font-bold text-2xl mt-6">{s.title}</h3>
                <p className="mt-3 text-[#8B93A5] leading-relaxed text-sm">{s.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== WHAT WE MONITOR ============== */}
      <section className="py-24 md:py-32 border-t border-white/10 bg-[#08080A]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <SectionHeader
            overline="Coverage"
            title="Enterprise infrastructure & business-critical systems."
            subtitle="One monitoring plane across servers, enterprise services, databases, virtualisation, applications, cloud, storage and networking."
          />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {TECH_GRID.map((cat, i) => (
              <motion.div
                key={cat.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: i * 0.05 }}
                className="tracing-card group bg-[#0A0A0D] border border-white/10 hover:border-[#00F0FF]/50 p-6 transition-all"
                data-testid={`infra-tech-${cat.title.toLowerCase().replace(/\s|&/g, '-')}`}
              >
                <div className="w-11 h-11 border border-white/15 flex items-center justify-center mb-5 group-hover:border-[#39FF14] transition-colors">
                  <cat.icon className="w-5 h-5 text-[#00F0FF] group-hover:text-[#39FF14] transition-colors" />
                </div>
                <h3 className="font-display font-bold text-lg tracking-tight">{cat.title}</h3>
                <ul className="mt-4 space-y-2">
                  {cat.items.map((it) => (
                    <li key={it} className="flex items-center gap-2 text-sm text-[#8B93A5]">
                      <span className="w-1 h-1 bg-[#39FF14]" />
                      {it}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== LIGHTWEIGHT ============== */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <SectionHeader
              overline="Efficiency"
              title="Monitor More. Keep It Lightweight."
              subtitle="Quasect is designed to provide meaningful infrastructure visibility while keeping monitoring deployment and operational overhead low."
            />
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4 md:gap-5">
            {LIGHTWEIGHT.map((b, i) => (
              <motion.div
                key={b}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="bg-[#0A0A0D] border border-white/10 hover:border-[#39FF14]/40 p-5 flex items-center gap-3 transition-colors"
              >
                <CheckCircle2 className="w-5 h-5 text-[#39FF14] shrink-0" />
                <span className="text-white/90">{b}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== CUSTOM MONITORING RULE ============== */}
      <section className="py-24 md:py-32 border-t border-white/10 bg-[#08080A]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <SectionHeader
            overline="Custom Monitoring"
            title="Your Infrastructure. Your Monitoring Rules."
            subtitle="Different environments require different monitoring strategies. Quasect allows administrators to customise monitoring according to their infrastructure and business requirements."
          />

          {/* Config card */}
          <div className="mt-14 max-w-4xl mx-auto">
            <div className="border border-white/10 bg-[#0A0A0D] noise relative overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 bg-black/50">
                <div className="font-mono text-[10px] tracking-widest text-[#00F0FF] flex items-center gap-2">
                  <Wrench className="w-3.5 h-3.5" /> MONITORING RULE · draft
                </div>
                <div className="font-mono text-[10px] text-[#8B93A5]">RULE-4218</div>
              </div>
              <div className="p-6 md:p-8 grid md:grid-cols-2 gap-5">
                <ConfigRow k="Technology" v="SQL Server" />
                <ConfigRow k="Check" v="Database Connectivity" />
                <ConfigRow k="Interval" v="60 seconds" />
                <ConfigRow k="Threshold" v="Response time > 2 seconds" />
                <ConfigRow k="Priority" v="CRITICAL" accent="#EF4444" />
                <ConfigRow k="Action" v="Alert + Ticket" accent="#39FF14" />
              </div>
              <div className="px-6 md:px-8 py-4 border-t border-white/10 bg-black/30 flex items-center justify-between">
                <div className="font-mono text-[10px] tracking-widest text-[#8B93A5]">
                  ACTIVE · CHANGES SAVED
                </div>
                <div className="flex gap-2">
                  <span className="border border-white/15 px-3 py-1 font-mono text-[10px] tracking-widest text-white/70">
                    TEST
                  </span>
                  <span className="bg-[#39FF14] text-black px-3 py-1 font-mono text-[10px] font-bold tracking-widest">
                    ACTIVATE
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== PRIORITY-BASED MONITORING ============== */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <SectionHeader
            overline="Priority-Based Monitoring"
            title="Not Every System Is Equally Important."
            subtitle="Quasect helps your IT team focus attention on infrastructure that has the greatest business impact."
          />
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {PRIORITY_TIERS.map((t, i) => (
              <motion.div
                key={t.lvl}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07 }}
                className="bg-[#0A0A0D] border border-white/10 p-6 md:p-7"
                data-testid={`infra-priority-${t.lvl.toLowerCase()}`}
              >
                <div
                  className="font-mono text-xs tracking-[0.25em] uppercase font-bold inline-block px-2.5 py-1 border"
                  style={{ color: t.color, borderColor: t.color + "66", background: t.color + "11" }}
                >
                  {t.lvl}
                </div>
                <ul className="mt-5 space-y-2.5">
                  {t.items.map((it) => (
                    <li key={it} className="flex items-center gap-3 text-sm text-white/85">
                      <span className="w-1.5 h-1.5" style={{ background: t.color }} />
                      {it}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== ALERTING ============== */}
      <section className="py-24 md:py-32 border-t border-white/10 bg-[#08080A]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <SectionHeader
            overline="Alerting"
            title="From Detection to Action."
            subtitle="Every event flows through a consistent pipeline — monitor, detect, prioritise, alert, escalate, resolve — so nothing critical goes unnoticed."
          />

          {/* Workflow */}
          <div className="mt-14 flex items-center justify-between flex-wrap gap-3">
            {["Monitor", "Detect", "Prioritise", "Alert", "Escalate", "Resolve"].map((step, i, arr) => (
              <div key={step} className="flex items-center gap-3 grow">
                <div className="flex-1 border border-white/10 bg-[#0A0A0D] px-4 py-3 text-center">
                  <div className="font-mono text-[10px] tracking-widest text-[#8B93A5]">
                    STEP {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="font-display font-bold mt-1">{step}</div>
                </div>
                {i < arr.length - 1 && (
                  <ArrowRight className="w-5 h-5 text-[#39FF14] shrink-0 hidden md:block" />
                )}
              </div>
            ))}
          </div>

          {/* Alert example */}
          <div className="mt-14 max-w-3xl mx-auto border border-[#EF4444]/40 bg-[#0A0A0D] noise relative">
            <div className="absolute top-0 left-0 right-0 h-[3px] bg-[#EF4444]" />
            <div className="p-6 md:p-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 border border-[#EF4444]/50 bg-[#EF4444]/10 flex items-center justify-center shrink-0">
                  <AlertTriangle className="w-6 h-6 text-[#EF4444]" />
                </div>
                <div className="flex-1">
                  <div className="font-mono text-[11px] tracking-widest text-[#EF4444] font-bold">
                    CRITICAL · PRODUCTION SQL SERVER
                  </div>
                  <h3 className="font-display font-bold text-xl md:text-2xl mt-2">
                    Database response time exceeded configured threshold.
                  </h3>
                  <div className="mt-3 font-mono text-xs text-[#8B93A5]">
                    Duration: 8m 42s · Rule RULE-4218 · Server SQL-PROD-01
                  </div>
                  <div className="mt-5 flex gap-2 flex-wrap">
                    {[
                      { l: "Acknowledge", i: CheckCircle2 },
                      { l: "Investigate", i: Activity },
                      { l: "Create Ticket", i: Ticket },
                    ].map((a) => (
                      <span
                        key={a.l}
                        className="inline-flex items-center gap-2 border border-white/15 px-3 py-2 text-xs uppercase tracking-wider text-white/80"
                      >
                        <a.i className="w-3.5 h-3.5" /> {a.l}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== DASHBOARD PREVIEW ============== */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <SectionHeader
            overline="Dashboard Preview"
            title="One glance. Every layer of your stack."
            subtitle="A single-pane view of infrastructure health, active alerts, technology-level status, and performance trends."
          />
          <div className="mt-14">
            <FullDashboard />
          </div>
        </div>
      </section>

      {/* ============== CUSTOMISABILITY ============== */}
      <section className="py-24 md:py-32 border-t border-white/10 bg-[#08080A]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <SectionHeader
              overline="Customisability"
              title="Your infrastructure is unique. Your monitoring should be too."
              subtitle="Quasect lets you define every dimension of how, when and why systems are watched."
            />
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-3">
            {CUSTOMISE.map((c, i) => (
              <motion.div
                key={c}
                initial={{ opacity: 0, x: 12 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.04 }}
                className="bg-[#0A0A0D] border border-white/10 p-4 flex items-center gap-3 hover:border-[#00F0FF]/40 transition-colors"
              >
                <span className="font-mono text-[10px] tracking-widest text-[#39FF14] w-6">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-white/90">{c}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== FINAL CTA ============== */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="border border-white/10 bg-[#0A0A0D] p-10 md:p-16 grid lg:grid-cols-12 gap-8 items-center noise relative">
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#39FF14] to-transparent" />
            <div className="lg:col-span-8">
              <div className="font-mono text-xs tracking-[0.25em] uppercase text-[#00F0FF]">
                READY WHEN YOU ARE
              </div>
              <h2 className="font-display font-black text-4xl md:text-5xl mt-5 tracking-tighter leading-[1.05]">
                See Quasect Infrastructure Monitoring{" "}
                <span className="text-[#39FF14]">in your environment.</span>
              </h2>
              <p className="text-[#8B93A5] mt-5 max-w-2xl">
                Book a demo tailored to your stack. Our team will walk through configuration,
                priority tiers, alert routing and dashboards live.
              </p>
            </div>
            <div className="lg:col-span-4 flex flex-col gap-3">
              <Link
                to="/contact"
                data-testid="infra-final-cta-demo"
                className="btn-sweep bg-[#39FF14] text-black font-bold uppercase tracking-wider px-7 py-4 text-sm hover:bg-[#32E612] transition-colors flex items-center justify-center gap-2"
              >
                Request a Demo <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                data-testid="infra-final-cta-contact"
                className="border border-white/20 hover:border-white text-white px-7 py-4 text-sm uppercase tracking-wider transition-all flex items-center justify-center gap-2"
              >
                Contact Quasect
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// ============ Sub-components ============

function ConfigRow({ k, v, accent }) {
  return (
    <div className="border border-white/10 bg-black/30 p-4">
      <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#8B93A5]">{k}</div>
      <div
        className="mt-1.5 font-display font-bold text-lg tracking-tight"
        style={accent ? { color: accent } : {}}
      >
        {v}
      </div>
    </div>
  );
}

function MiniDashboard() {
  const stats = [
    { l: "Healthy", v: 214, c: "#39FF14" },
    { l: "Warning", v: 8, c: "#F59E0B" },
    { l: "Critical", v: 2, c: "#EF4444" },
    { l: "Offline", v: 1, c: "#8B93A5" },
  ];
  return (
    <div className="border border-white/10 bg-[#0A0A0D] noise relative">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/10 bg-black/40">
        <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest text-[#00F0FF]">
          <span className="w-1.5 h-1.5 bg-[#39FF14] pulse-dot" /> INFRA · LIVE
        </div>
        <div className="font-mono text-[10px] text-[#8B93A5]">225 ASSETS</div>
      </div>
      <div className="p-5">
        <div className="font-mono text-[10px] tracking-widest text-[#8B93A5]">
          OVERALL HEALTH
        </div>
        <div className="mt-2 flex items-baseline gap-2">
          <div className="font-display font-black text-5xl" style={{ color: "#39FF14" }}>
            96.4%
          </div>
          <div className="font-mono text-xs text-[#8B93A5]">+0.4 pt / 24h</div>
        </div>
        <div className="mt-5 grid grid-cols-4 gap-2">
          {stats.map((s) => (
            <div key={s.l} className="border border-white/10 p-2.5">
              <div className="font-mono text-[9px] tracking-widest text-[#8B93A5]">
                {s.l.toUpperCase()}
              </div>
              <div className="font-bold text-xl mt-0.5" style={{ color: s.c }}>
                {s.v}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 border border-white/10 p-3">
          <div className="font-mono text-[10px] tracking-widest text-[#8B93A5] mb-2">
            PERFORMANCE TREND · 24H
          </div>
          <div className="flex items-end gap-[3px] h-16">
            {Array.from({ length: 24 }).map((_, i) => {
              const h = 30 + ((i * 41) % 70);
              return (
                <div
                  key={i}
                  className="flex-1"
                  style={{
                    height: `${h}%`,
                    background: h > 75 ? "#39FF14" : h > 45 ? "#00F0FF" : "rgba(255,255,255,0.25)",
                  }}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function FullDashboard() {
  const tech = [
    { name: "Windows Servers",   ok: 42, warn: 1, crit: 0 },
    { name: "Linux Servers",     ok: 58, warn: 0, crit: 0 },
    { name: "SQL Databases",     ok: 12, warn: 2, crit: 1 },
    { name: "Active Directory",  ok: 6,  warn: 0, crit: 0 },
    { name: "Exchange",          ok: 4,  warn: 1, crit: 0 },
    { name: "VMware",            ok: 18, warn: 0, crit: 0 },
    { name: "Cloud Services",    ok: 33, warn: 2, crit: 1 },
    { name: "Backup",            ok: 8,  warn: 2, crit: 0 },
  ];

  return (
    <div className="border border-white/10 bg-[#0A0A0D] noise relative overflow-hidden">
      {/* Top bar */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 bg-black/40 flex-wrap gap-2">
        <div className="flex items-center gap-3 font-mono text-[10px] tracking-widest text-[#00F0FF]">
          <span className="w-1.5 h-1.5 bg-[#39FF14] pulse-dot" /> QUASECT · INFRASTRUCTURE MONITORING
        </div>
        <div className="font-mono text-[10px] text-[#8B93A5]">TENANT · ACME CORP · UTC 14:22</div>
      </div>

      {/* Top row: 4 KPI cards */}
      <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { l: "Overall Health", v: "96.4%", c: "#39FF14" },
          { l: "Monitored Assets", v: "225", c: "#fff" },
          { l: "Active Alerts", v: "3", c: "#EF4444" },
          { l: "Availability", v: "99.98%", c: "#00F0FF" },
        ].map((k) => (
          <div key={k.l} className="border border-white/10 p-4 bg-black/30">
            <div className="font-mono text-[10px] tracking-widest text-[#8B93A5]">
              {k.l.toUpperCase()}
            </div>
            <div className="font-display font-bold text-3xl mt-1.5" style={{ color: k.c }}>
              {k.v}
            </div>
          </div>
        ))}
      </div>

      {/* Middle: status pills */}
      <div className="px-5 grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { l: "Healthy", v: 214, c: "#39FF14" },
          { l: "Warning", v: 8,   c: "#F59E0B" },
          { l: "Critical", v: 2,  c: "#EF4444" },
          { l: "Offline", v: 1,   c: "#8B93A5" },
        ].map((s) => (
          <div
            key={s.l}
            className="border p-4"
            style={{ borderColor: s.c + "44", background: s.c + "0a" }}
          >
            <div className="font-mono text-[10px] tracking-widest" style={{ color: s.c }}>
              {s.l.toUpperCase()}
            </div>
            <div className="font-display font-bold text-2xl mt-1" style={{ color: s.c }}>
              {s.v}
            </div>
          </div>
        ))}
      </div>

      {/* Tech grid + trend */}
      <div className="p-5 grid lg:grid-cols-3 gap-4">
        {/* Tech health */}
        <div className="lg:col-span-2 border border-white/10 bg-black/30 p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="font-mono text-[10px] tracking-widest text-[#00F0FF]">
              TECHNOLOGY HEALTH
            </div>
            <div className="font-mono text-[10px] text-[#8B93A5]">SORTED BY IMPACT</div>
          </div>
          <div className="space-y-2">
            {tech.map((t) => {
              const total = t.ok + t.warn + t.crit;
              const okPct = (t.ok / total) * 100;
              const warnPct = (t.warn / total) * 100;
              const critPct = (t.crit / total) * 100;
              return (
                <div key={t.name} className="grid grid-cols-12 gap-3 items-center">
                  <div className="col-span-4 font-mono text-xs text-white/90">{t.name}</div>
                  <div className="col-span-6 h-3 bg-white/5 flex">
                    <div style={{ width: `${okPct}%`, background: "#39FF14" }} />
                    <div style={{ width: `${warnPct}%`, background: "#F59E0B" }} />
                    <div style={{ width: `${critPct}%`, background: "#EF4444" }} />
                  </div>
                  <div className="col-span-2 font-mono text-[10px] text-[#8B93A5] text-right">
                    {t.ok}·{t.warn}·{t.crit}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Performance trend */}
        <div className="border border-white/10 bg-black/30 p-4 flex flex-col">
          <div className="font-mono text-[10px] tracking-widest text-[#00F0FF]">
            PERFORMANCE TRENDS
          </div>
          <div className="mt-2 font-mono text-[9px] text-[#8B93A5]">RESPONSE · LAST 24H</div>
          <div className="mt-4 flex items-end gap-[3px] h-32 flex-1">
            {Array.from({ length: 32 }).map((_, i) => {
              const h = 20 + ((i * 43) % 80);
              return (
                <div
                  key={i}
                  className="flex-1"
                  style={{
                    height: `${h}%`,
                    background: h > 75 ? "#39FF14" : h > 45 ? "#00F0FF" : "rgba(255,255,255,0.2)",
                  }}
                />
              );
            })}
          </div>
          <div className="mt-3 flex items-center justify-between font-mono text-[10px] text-[#8B93A5]">
            <span>P50 · 42ms</span>
            <span className="text-[#39FF14]">P95 · 218ms</span>
          </div>
        </div>
      </div>

      {/* Alerts strip */}
      <div className="border-t border-white/10 px-5 py-4 bg-black/40">
        <div className="font-mono text-[10px] tracking-widest text-[#00F0FF] mb-3">
          ACTIVE ALERTS · 3
        </div>
        <div className="space-y-1.5">
          {[
            { p: "CRITICAL", c: "#EF4444", m: "Production SQL · response > 2s threshold breach", d: "8m 42s" },
            { p: "HIGH",     c: "#F59E0B", m: "Exchange · queue length > 500 messages",           d: "2m 11s" },
            { p: "HIGH",     c: "#F59E0B", m: "Cloud egress spike · 3.2× baseline",              d: "12m 03s" },
          ].map((a, i) => (
            <div key={i} className="flex items-center gap-3 border border-white/10 px-3 py-2 bg-black/50 font-mono text-xs">
              <span className="font-bold px-2 py-0.5 text-[10px] tracking-widest"
                style={{ background: a.c + "22", color: a.c, border: `1px solid ${a.c}66` }}>
                {a.p}
              </span>
              <span className="text-white/85 flex-1 truncate">{a.m}</span>
              <span className="text-[#8B93A5]">{a.d}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
